/**
 * Educational calculation helpers for the Hybrid System Integration Tool.
 * The formulas are transparent first-draft approximations for learning,
 * not certified engineering sizing calculations.
 * @module HybridSystemIntegration/utils
 */

import {
  COMPONENT_TYPES,
  DEFAULT_SYSTEM_SETTINGS,
  INSULATION_PRESETS,
} from './constants';

/**
 * Clamp a numeric value between two limits.
 * @param {number} value - Raw value.
 * @param {number} min - Minimum allowed value.
 * @param {number} max - Maximum allowed value.
 * @returns {number} Clamped value.
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));
}

/**
 * Round a number for UI display.
 * @param {number} value - Raw value.
 * @param {number} decimals - Number of decimals.
 * @returns {number} Rounded value.
 */
export function roundMetric(value, decimals = 1) {
  const factor = 10 ** decimals;
  return Math.round((Number(value) || 0) * factor) / factor;
}

/**
 * Find the first node of one of the requested component types.
 * @param {Array<Object>} nodes - Canvas nodes.
 * @param {string|string[]} types - Component type or list of types.
 * @returns {Object|null} Matching node.
 */
export function findNodeByType(nodes, types) {
  const typeList = Array.isArray(types) ? types : [types];
  return nodes.find((node) => typeList.includes(node.type)) || null;
}

/**
 * Calculate a simple heat pump COP approximation.
 * @param {Object} params - COP parameters.
 * @param {number} params.outdoorTemperature - Outdoor temperature in °C.
 * @param {number} params.supplyTemperature - Supply temperature in °C.
 * @param {string} params.heatPumpType - Component type.
 * @returns {number} Estimated COP.
 */
export function calculateHeatPumpCOP({ outdoorTemperature, supplyTemperature, heatPumpType }) {
  const sourceTemperature = heatPumpType === COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP
    ? Math.max(4, outdoorTemperature + 9)
    : outdoorTemperature;
  const lift = Math.max(12, supplyTemperature - sourceTemperature);
  const baseCop = 6.2 - lift * 0.07;
  const sourceBonus = heatPumpType === COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP ? 0.35 : 0;

  return clamp(baseCop + sourceBonus, 1.15, 5.8);
}

/**
 * Calculate building heat demand from floor area, insulation, and temperature lift.
 * @param {Object} params - Building parameters.
 * @returns {number} Heat demand in kW.
 */
export function calculateBuildingHeatDemand({
  outdoorTemperature,
  indoorTemperature,
  floorArea,
  insulationLevel,
}) {
  const preset = INSULATION_PRESETS[insulationLevel] || INSULATION_PRESETS.retrofit;
  const deltaT = Math.max(0, indoorTemperature - outdoorTemperature);
  const fabricLoadKw = (floorArea * preset.heatLossWPerM2K * deltaT) / 1000;
  const domesticHotWaterKw = Math.max(0.8, floorArea / 180);

  return Math.max(0.4, fabricLoadKw + domesticHotWaterKw);
}

/**
 * Estimate current PV production from installed capacity, hour, and irradiance slider.
 * @param {Object} params - PV parameters.
 * @returns {number} PV output in kW.
 */
export function calculateSolarPVOutput({ capacityKw, hourOfDay, solarIrradiance }) {
  const daylightShape = Math.sin((Math.PI * clamp(hourOfDay, 0, 24)) / 24);
  const noonBoost = Math.max(0, daylightShape) ** 1.45;

  return capacityKw * noonBoost * clamp(solarIrradiance, 0, 1);
}

/**
 * Apply a temperature derating curve to nominal heat pump capacity.
 * @param {number} ratedCapacityKw - Nominal capacity in kW.
 * @param {number} outdoorTemperature - Outdoor temperature in °C.
 * @param {string} heatPumpType - Component type.
 * @returns {number} Available heat pump capacity in kW.
 */
export function calculateAvailableHeatPumpCapacity(ratedCapacityKw, outdoorTemperature, heatPumpType) {
  if (heatPumpType === COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP) {
    return ratedCapacityKw * 0.96;
  }

  const derating = 1 - Math.max(0, 7 - outdoorTemperature) * 0.018;
  return ratedCapacityKw * clamp(derating, 0.62, 1.05);
}

/**
 * Decide the active load split for a hybrid heating system.
 * @param {Object} params - Dispatch inputs.
 * @returns {Object} Dispatch results and KPIs.
 */
export function calculateHybridDispatch(params) {
  const {
    heatDemandKw,
    heatPumpCapacityKw,
    heatPumpType,
    cop,
    strategy,
    outdoorTemperature,
    bivalenceTemperature,
    boilerCapacityKw,
    boilerEfficiency,
    pvOutputKw,
    storageCapacityKwh,
    storageLevel,
    storageMaxDischargeKw,
    electricityPrice,
    gasPrice,
    electricityCarbonIntensity,
    gasCarbonIntensity,
  } = params;

  const hpCostPerHeat = electricityPrice / Math.max(cop, 0.1);
  const boilerCostPerHeat = gasPrice / Math.max(boilerEfficiency, 0.1);
  const hpCarbonPerHeat = electricityCarbonIntensity / Math.max(cop, 0.1);
  const boilerCarbonPerHeat = gasCarbonIntensity / Math.max(boilerEfficiency, 0.1);
  const availableHeatPumpKw = calculateAvailableHeatPumpCapacity(
    heatPumpCapacityKw,
    outdoorTemperature,
    heatPumpType
  );

  let heatPumpHeatKw = 0;
  let strategyReason = 'priority';

  switch (strategy) {
    case 'bivalentParallel':
      heatPumpHeatKw = Math.min(availableHeatPumpKw, heatDemandKw * (outdoorTemperature < bivalenceTemperature ? 0.6 : 0.9));
      strategyReason = 'parallel';
      break;
    case 'bivalentAlternative':
      heatPumpHeatKw = outdoorTemperature < bivalenceTemperature ? 0 : Math.min(availableHeatPumpKw, heatDemandKw);
      strategyReason = outdoorTemperature < bivalenceTemperature ? 'alternativeBackup' : 'alternativeHeatPump';
      break;
    case 'costOptimized':
      heatPumpHeatKw = hpCostPerHeat <= boilerCostPerHeat ? Math.min(availableHeatPumpKw, heatDemandKw) : 0;
      strategyReason = hpCostPerHeat <= boilerCostPerHeat ? 'costHeatPump' : 'costBoiler';
      break;
    case 'co2Optimized':
      heatPumpHeatKw = hpCarbonPerHeat <= boilerCarbonPerHeat ? Math.min(availableHeatPumpKw, heatDemandKw) : 0;
      strategyReason = hpCarbonPerHeat <= boilerCarbonPerHeat ? 'carbonHeatPump' : 'carbonBoiler';
      break;
    case 'heatPumpPriority':
    default:
      heatPumpHeatKw = Math.min(availableHeatPumpKw, heatDemandKw);
      strategyReason = 'priority';
      break;
  }

  const storageAvailableKw = storageCapacityKwh > 0
    ? Math.min(storageMaxDischargeKw, (storageCapacityKwh * clamp(storageLevel, 0, 100)) / 100 / 3)
    : 0;
  const remainingAfterHeatPump = Math.max(0, heatDemandKw - heatPumpHeatKw);
  const storageDischargeKw = Math.min(remainingAfterHeatPump, storageAvailableKw);
  const remainingAfterStorage = Math.max(0, remainingAfterHeatPump - storageDischargeKw);
  const boilerHeatKw = Math.min(boilerCapacityKw, remainingAfterStorage);
  const unmetLoadKw = Math.max(0, heatDemandKw - heatPumpHeatKw - storageDischargeKw - boilerHeatKw);
  const heatPumpElectricKw = heatPumpHeatKw / Math.max(cop, 0.1);
  const pvToHeatPumpKw = Math.min(pvOutputKw, heatPumpElectricKw);
  const gridElectricKw = Math.max(0, heatPumpElectricKw - pvToHeatPumpKw);
  const gasInputKw = boilerHeatKw / Math.max(boilerEfficiency, 0.1);
  const costPerHour = gridElectricKw * electricityPrice + gasInputKw * gasPrice;
  const carbonKgPerHour = gridElectricKw * electricityCarbonIntensity + gasInputKw * gasCarbonIntensity;
  const deliveredHeatKw = heatPumpHeatKw + storageDischargeKw + boilerHeatKw;
  const renewableHeatKw = heatPumpHeatKw * Math.max(0, 1 - 1 / Math.max(cop, 1)) + storageDischargeKw + pvToHeatPumpKw;

  return {
    strategyReason,
    heatDemandKw,
    deliveredHeatKw,
    heatPumpHeatKw,
    boilerHeatKw,
    storageDischargeKw,
    unmetLoadKw,
    heatPumpElectricKw,
    pvOutputKw,
    pvToHeatPumpKw,
    gridElectricKw,
    gasInputKw,
    costPerHour,
    carbonKgPerHour,
    cop,
    availableHeatPumpKw,
    heatPumpCoveragePercent: heatDemandKw > 0 ? (heatPumpHeatKw / heatDemandKw) * 100 : 0,
    boilerCoveragePercent: heatDemandKw > 0 ? (boilerHeatKw / heatDemandKw) * 100 : 0,
    storageCoveragePercent: heatDemandKw > 0 ? (storageDischargeKw / heatDemandKw) * 100 : 0,
    renewableSharePercent: heatDemandKw > 0 ? clamp((renewableHeatKw / heatDemandKw) * 100, 0, 100) : 0,
    systemEfficiencyPercent: heatDemandKw > 0 ? clamp((deliveredHeatKw / (gridElectricKw + gasInputKw + pvToHeatPumpKw + 0.001)) * 100, 0, 580) : 0,
    hpCostPerHeat,
    boilerCostPerHeat,
    hpCarbonPerHeat,
    boilerCarbonPerHeat,
  };
}

/**
 * Build all KPI results from current nodes and settings.
 * @param {Object} params - State inputs.
 * @returns {Object} Simulation snapshot.
 */
export function calculateSystemSnapshot({ nodes, settings, strategy, storageLevel }) {
  const heatPumpNode = findNodeByType(nodes, [
    COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP,
    COMPONENT_TYPES.GROUND_SOURCE_HEAT_PUMP,
  ]);
  const boilerNode = findNodeByType(nodes, COMPONENT_TYPES.GAS_BOILER);
  const pvNode = findNodeByType(nodes, COMPONENT_TYPES.SOLAR_PV);
  const storageNode = findNodeByType(nodes, COMPONENT_TYPES.THERMAL_STORAGE);

  const heatPumpSpecs = heatPumpNode?.specs || {};
  const boilerSpecs = boilerNode?.specs || {};
  const pvSpecs = pvNode?.specs || {};
  const storageSpecs = storageNode?.specs || {};
  const supplyTemperature = heatPumpSpecs.supplyTemperature ?? 45;
  const heatPumpType = heatPumpNode?.type || COMPONENT_TYPES.AIR_SOURCE_HEAT_PUMP;
  const cop = heatPumpNode
    ? calculateHeatPumpCOP({
        outdoorTemperature: settings.outdoorTemperature,
        supplyTemperature,
        heatPumpType,
      })
    : 1;
  const heatDemandKw = calculateBuildingHeatDemand(settings);
  const pvOutputKw = pvNode
    ? calculateSolarPVOutput({
        capacityKw: pvSpecs.capacityKw ?? 0,
        hourOfDay: settings.hourOfDay,
        solarIrradiance: settings.solarIrradiance,
      })
    : 0;

  return calculateHybridDispatch({
    heatDemandKw,
    heatPumpCapacityKw: heatPumpNode ? heatPumpSpecs.ratedCapacityKw ?? 0 : 0,
    heatPumpType,
    cop,
    strategy,
    outdoorTemperature: settings.outdoorTemperature,
    bivalenceTemperature: heatPumpSpecs.bivalenceTemperature ?? -4,
    boilerCapacityKw: boilerNode ? boilerSpecs.ratedCapacityKw ?? 0 : 0,
    boilerEfficiency: boilerSpecs.efficiency ?? 0.92,
    pvOutputKw,
    storageCapacityKwh: storageNode ? storageSpecs.capacityKwh ?? 0 : 0,
    storageLevel: storageNode ? storageLevel : 0,
    storageMaxDischargeKw: storageSpecs.maxDischargeKw ?? 0,
    electricityPrice: settings.electricityPrice,
    gasPrice: settings.gasPrice,
    electricityCarbonIntensity: settings.electricityCarbonIntensity,
    gasCarbonIntensity: settings.gasCarbonIntensity,
  });
}

/**
 * Generate a 24-hour educational timeline for charts and playback.
 * @param {Object} params - Timeline inputs.
 * @returns {Array<Object>} Hourly results.
 */
export function generateTimeline({ nodes, settings, strategy, storageLevel }) {
  return Array.from({ length: 24 }, (_, hour) => {
    const outdoorSwing = Math.sin(((hour - 5) / 24) * Math.PI * 2) * 4.2;
    const solarShape = Math.max(0, Math.sin((Math.PI * hour) / 24));
    const hourSettings = {
      ...settings,
      hourOfDay: hour,
      outdoorTemperature: settings.outdoorTemperature + outdoorSwing,
      solarIrradiance: settings.solarIrradiance * solarShape,
      electricityPrice: hour >= 17 && hour <= 20 ? settings.electricityPrice * 1.35 : settings.electricityPrice,
    };
    const result = calculateSystemSnapshot({ nodes, settings: hourSettings, strategy, storageLevel });

    return {
      hour,
      label: `${hour}:00`,
      demand: roundMetric(result.heatDemandKw, 2),
      heatPump: roundMetric(result.heatPumpHeatKw, 2),
      boiler: roundMetric(result.boilerHeatKw, 2),
      storage: roundMetric(result.storageDischargeKw, 2),
      cost: roundMetric(result.costPerHour, 2),
      carbon: roundMetric(result.carbonKgPerHour, 2),
      cop: roundMetric(result.cop, 2),
    };
  });
}

/**
 * Build rule-based recommendations for the current educational scenario.
 * @param {Object} result - Simulation result snapshot.
 * @param {Object} settings - Current settings.
 * @returns {Array<Object>} Recommendation cards.
 */
export function buildRecommendations(result, settings) {
  const recommendations = [];

  if (result.unmetLoadKw > 0.2) {
    recommendations.push({ type: 'error', key: 'unmetLoad' });
  }

  if (result.cop < 2.2) {
    recommendations.push({ type: 'warning', key: 'lowCop' });
  }

  if (result.boilerCoveragePercent > 45) {
    recommendations.push({ type: 'warning', key: 'boilerHeavy' });
  }

  if (result.pvOutputKw > result.pvToHeatPumpKw + 1 && result.storageCoveragePercent < 5) {
    recommendations.push({ type: 'info', key: 'storageOpportunity' });
  }

  if (settings.electricityPrice > 0.45 && result.hpCostPerHeat > result.boilerCostPerHeat) {
    recommendations.push({ type: 'info', key: 'tariffAware' });
  }

  if (result.renewableSharePercent >= 70 && result.unmetLoadKw < 0.1) {
    recommendations.push({ type: 'success', key: 'lowCarbon' });
  }

  if (recommendations.length === 0) {
    recommendations.push({ type: 'success', key: 'balanced' });
  }

  return recommendations;
}

/**
 * Create an initial settings object with safe defaults.
 * @returns {Object} Default settings clone.
 */
export function createDefaultSettings() {
  return { ...DEFAULT_SYSTEM_SETTINGS };
}
